/**
 * $MANGO Token & Membership Contract Configuration
 * 
 * These contracts are deployed on Base (Chain ID: 8453) and Base Sepolia (84532)
 * Update addresses after deployment
 */

// Contract Addresses (UPDATE AFTER DEPLOYMENT)
export const MANGO_CONTRACTS = {
  // Mainnet (Base)
  mainnet: {
    chainId: 8453,
    MangoToken: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoMembership: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoStaking: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoTreasury: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
  },
  // Testnet (Base Sepolia)
  testnet: {
    chainId: 84532,
    MangoToken: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoMembership: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoStaking: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
    MangoTreasury: '0x0000000000000000000000000000000000000000', // TODO: Update after deployment
  },
} as const

// Use testnet by default in development
export const ACTIVE_NETWORK = import.meta.env.PROD ? 'mainnet' : 'testnet'
export const CONTRACTS = MANGO_CONTRACTS[ACTIVE_NETWORK]

// MangoToken ABI (ERC20 + Custom Functions)
export const MANGO_TOKEN_ABI = [
  // ERC20 Standard
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) returns (bool)',
  
  // ERC20 Burnable
  'function burn(uint256 amount)',
  'function burnFrom(address account, uint256 amount)',
  
  // ERC20 Permit (Gasless Approvals)
  'function permit(address owner, address spender, uint256 value, uint256 deadline, uint8 v, bytes32 r, bytes32 s)',
  'function nonces(address owner) view returns (uint256)',
  'function DOMAIN_SEPARATOR() view returns (bytes32)',
  
  // Custom MANGO functions
  'function MAX_SUPPLY() view returns (uint256)',
  'function INITIAL_MINT() view returns (uint256)',
  'function totalBurned() view returns (uint256)',
  'function treasury() view returns (address)',
  'function isExempt(address) view returns (bool)',
  
  // Events
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)',
  'event BurnedTokens(address indexed burner, uint256 amount)',
] as const

// MangoMembership ABI (ERC721 + Custom Functions)
export const MANGO_MEMBERSHIP_ABI = [
  // ERC721 Standard
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function balanceOf(address owner) view returns (uint256)',
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function safeTransferFrom(address from, address to, uint256 tokenId)',
  'function transferFrom(address from, address to, uint256 tokenId)',
  'function approve(address to, uint256 tokenId)',
  'function setApprovalForAll(address operator, bool approved)',
  'function getApproved(uint256 tokenId) view returns (address)',
  'function isApprovedForAll(address owner, address operator) view returns (bool)',
  'function tokenURI(uint256 tokenId) view returns (string)',
  
  // ERC721 Enumerable
  'function totalSupply() view returns (uint256)',
  'function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)',
  'function tokenByIndex(uint256 index) view returns (uint256)',
  
  // Custom Membership functions
  'function mintWithETH(uint8 tier) payable',
  'function mintWithMANGO(uint8 tier)',
  'function upgradeTier(uint256 tokenId, uint8 newTier) payable',
  'function upgradeTierWithMANGO(uint256 tokenId, uint8 newTier)',
  'function tokenTier(uint256 tokenId) view returns (uint8)',
  'function tierMinted(uint8 tier) view returns (uint256)',
  'function getUserTier(address user) view returns (uint8)',
  'function hasMinimumTier(address user, uint8 minTier) view returns (bool)',
  
  // Tier prices
  'function BASIC_PRICE_ETH() view returns (uint256)',
  'function PREMIUM_PRICE_ETH() view returns (uint256)',
  'function VIP_PRICE_ETH() view returns (uint256)',
  'function FOUNDERS_PRICE_ETH() view returns (uint256)',
  'function BASIC_PRICE_MANGO() view returns (uint256)',
  'function PREMIUM_PRICE_MANGO() view returns (uint256)',
  'function VIP_PRICE_MANGO() view returns (uint256)',
  'function FOUNDERS_PRICE_MANGO() view returns (uint256)',
  
  // Tier supply caps
  'function BASIC_SUPPLY() view returns (uint256)',
  'function PREMIUM_SUPPLY() view returns (uint256)',
  'function VIP_SUPPLY() view returns (uint256)',
  'function FOUNDERS_SUPPLY() view returns (uint256)',
  
  // Revenue sharing
  'function claimRevenue()',
  'function getClaimableRevenue(address user) view returns (uint256)',
  'function revenuePool() view returns (uint256)',
  'function totalRevenueDistributed() view returns (uint256)',
  
  // Events
  'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
  'event Approval(address indexed owner, address indexed approved, uint256 indexed tokenId)',
  'event MembershipMinted(address indexed to, uint256 indexed tokenId, uint8 tier, bool paidWithToken)',
  'event MembershipUpgraded(uint256 indexed tokenId, uint8 oldTier, uint8 newTier)',
  'event RevenueClaimed(address indexed user, uint256 amount)',
] as const

// Membership Tier enum (matches contract)
export enum MembershipTier {
  BASIC = 0,
  PREMIUM = 1,
  VIP = 2,
  FOUNDERS = 3,
}

// Tier display info
export const TIER_INFO = {
  [MembershipTier.BASIC]: {
    name: 'Basic',
    icon: '🥉',
    priceETH: '0.005',
    priceMANGO: '3,000',
    maxSupply: 10000,
    benefits: [
      'Access to basic content',
      'Standard video quality',
      'Community chat access',
    ],
    revenueShare: '0%',
  },
  [MembershipTier.PREMIUM]: {
    name: 'Premium',
    icon: '🥈',
    priceETH: '0.025',
    priceMANGO: '15,000',
    maxSupply: 5000,
    benefits: [
      'All Basic benefits',
      'Premium exclusive content',
      'HD video quality',
      'Priority support',
    ],
    revenueShare: '0%',
  },
  [MembershipTier.VIP]: {
    name: 'VIP',
    icon: '🥇',
    priceETH: '0.1',
    priceMANGO: '60,000',
    maxSupply: 1000,
    benefits: [
      'All Premium benefits',
      'VIP exclusive content',
      '4K video quality',
      'Direct model chat',
      'Early access to new content',
      'Revenue share (0.5%)',
    ],
    revenueShare: '0.5%',
  },
  [MembershipTier.FOUNDERS]: {
    name: 'Founders',
    icon: '💎',
    priceETH: '0.5',
    priceMANGO: '300,000',
    maxSupply: 100,
    benefits: [
      'All VIP benefits',
      'Founders-only content',
      'Governance voting rights',
      'Platform fee discounts',
      'Exclusive events access',
      'Revenue share (2%)',
    ],
    revenueShare: '2%',
  },
} as const

// Token constants
export const MANGO_DECIMALS = 18
export const MAX_MANGO_SUPPLY = 1_000_000_000n * BigInt(10 ** MANGO_DECIMALS)

// Helper to format MANGO amounts
export function formatMango(amount: bigint | number): string {
  const value = typeof amount === 'bigint' ? amount : BigInt(amount)
  return (Number(value) / 10 ** MANGO_DECIMALS).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })
}

// Helper to parse MANGO amounts
export function parseMango(amount: string | number): bigint {
  return BigInt(Math.floor(Number(amount) * 10 ** MANGO_DECIMALS))
}
